import { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "../../../DataTable/DataTable";
import {
  MasterOption,
  MasterRow,
  ProductCatalogService,
  SaltDrugMappingRow,
  ScheduleRow,
} from "../../../../services/api/productCatalog.service";
import {
  FilterSearchSelect,
  FilterSelect,
  MasterPanel,
  StatusPill,
} from "./MasterPanel";

type InnerTab = "salt" | "drug" | "mapping" | "schedule";

const INNER_TABS: { id: InnerTab; label: string }[] = [
  { id: "salt", label: "Salt Details" },
  { id: "drug", label: "Drug Details" },
  { id: "mapping", label: "Salt - Drug Mapping" },
  { id: "schedule", label: "Medicine Schedule" },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const toBool = (value: string): boolean | undefined =>
  value === "" ? undefined : value === "true";

const asSelectOptions = (options: MasterOption[]) =>
  options.map((option) => ({ value: option.id, label: option.name }));

/**
 * Salt and drug masters, the mapping between them, and the medicine schedules.
 *
 * Salts are the active ingredients a product's composition is built from; the
 * product form picks salt ids from this master. Drugs are the clinical entities
 * a salt belongs to, mapped many-to-many.
 */
export function SaltMappingTab() {
  const [innerTab, setInnerTab] = useState<InnerTab>("salt");
  const [activeFilter, setActiveFilter] = useState("");

  const [saltOptions, setSaltOptions] = useState<MasterOption[]>([]);
  const [drugOptions, setDrugOptions] = useState<MasterOption[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const [filterSalt, setFilterSalt] = useState("");
  const [filterDrug, setFilterDrug] = useState("");

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      const [salts, drugs] = await Promise.all([
        ProductCatalogService.getSaltOptions({ isActive: true }),
        ProductCatalogService.getDrugOptions({ isActive: true }),
      ]);
      if (cancelled) return;
      setSaltOptions(salts?.success ? salts.data || [] : []);
      setDrugOptions(drugs?.success ? drugs.data || [] : []);
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const nameColumns: Column<MasterRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Name",
        width: "w-[380px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">{row.name}</span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
      {
        key: "createdAt",
        title: "Created",
        width: "w-[140px]",
        render: (row) => (
          <span className="text-sm text-gray-500">
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}
          </span>
        ),
      },
    ],
    []
  );

  const mappingColumns: Column<SaltDrugMappingRow>[] = useMemo(
    () => [
      {
        key: "saltName",
        title: "Salt",
        width: "w-[300px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">
            {row.saltName || row.saltId}
          </span>
        ),
      },
      {
        key: "drugName",
        title: "Drug",
        width: "w-[300px]",
        render: (row) => (
          <span className="text-sm text-gray-700">
            {row.drugName || row.drugId}
          </span>
        ),
      },
    ],
    []
  );

  const scheduleColumns: Column<ScheduleRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Schedule",
        width: "w-[200px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">{row.name}</span>
        ),
      },
      {
        key: "description",
        title: "Description",
        width: "w-[400px]",
        render: (row) => (
          <span className="text-sm text-gray-600" title={row.description || ""}>
            {row.description || "-"}
          </span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
    ],
    []
  );

  const fetchSalts = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getSalts({
        page,
        pageSize,
        search,
        isActive: toBool(activeFilter),
      }),
    [activeFilter]
  );

  const fetchDrugs = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getDrugs({
        page,
        pageSize,
        search,
        isActive: toBool(activeFilter),
      }),
    [activeFilter]
  );

  const fetchMappings = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) =>
      ProductCatalogService.getSaltDrugMappings({
        page,
        pageSize,
        saltId: filterSalt || undefined,
        drugId: filterDrug || undefined,
      }),
    [filterSalt, filterDrug]
  );

  const fetchSchedules = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getSchedules({
        page,
        pageSize,
        search,
        isActive: toBool(activeFilter),
      }),
    [activeFilter]
  );

  const statusFilterControl = (
    <FilterSelect
      label="Status"
      value={activeFilter}
      onChange={setActiveFilter}
      options={ACTIVE_FILTER_OPTIONS}
      width="w-32"
    />
  );

  return (
    <div className="space-y-5">
      <nav className="flex flex-wrap gap-2">
        {INNER_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setInnerTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
              innerTab === tab.id
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        {innerTab === "salt" && (
          <MasterPanel<MasterRow>
            title="Salt Details"
            description="Active ingredients. A product's composition is a set of these with their strengths."
            addLabel="Add Salt"
            columns={nameColumns}
            searchPlaceholder="Search salts..."
            filters={statusFilterControl}
            refreshToken={`${activeFilter}|${reloadToken}`}
            fetchRows={fetchSalts}
            fields={[
              {
                name: "name",
                label: "Salt Name",
                type: "text",
                required: true,
                placeholder: "e.g. Paracetamol",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ name: row.name, isActive: row.isActive })}
            onCreate={async (values) => {
              const res = await ProductCatalogService.createSaltMaster({
                name: values.name,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (row, values) => {
              const res = await ProductCatalogService.updateSaltMaster(
                String(row.id),
                { name: values.name, isActive: values.isActive }
              );
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}

        {innerTab === "drug" && (
          <MasterPanel<MasterRow>
            title="Drug Details"
            description="Clinical drug entities that salts are mapped to."
            addLabel="Add Drug"
            columns={nameColumns}
            searchPlaceholder="Search drugs..."
            filters={statusFilterControl}
            refreshToken={`${activeFilter}|${reloadToken}`}
            fetchRows={fetchDrugs}
            fields={[
              {
                name: "name",
                label: "Drug Name",
                type: "text",
                required: true,
                placeholder: "e.g. Acetaminophen",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ name: row.name, isActive: row.isActive })}
            onCreate={async (values) => {
              const res = await ProductCatalogService.createDrug({
                name: values.name,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (row, values) => {
              const res = await ProductCatalogService.updateDrug(
                String(row.id),
                { name: values.name, isActive: values.isActive }
              );
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}

        {innerTab === "mapping" && (
          <MasterPanel<SaltDrugMappingRow>
            title="Salt - Drug Mapping"
            description="Which drug each salt belongs to. Many-to-many: a salt can map to several drugs."
            addLabel="Add Mapping"
            columns={mappingColumns}
            searchable={false}
            refreshToken={`${filterSalt}|${filterDrug}|${reloadToken}`}
            fetchRows={fetchMappings}
            filters={
              <>
                <FilterSearchSelect
                  label="Salt"
                  value={filterSalt}
                  onChange={setFilterSalt}
                  options={asSelectOptions(saltOptions)}
                />
                <FilterSearchSelect
                  label="Drug"
                  value={filterDrug}
                  onChange={setFilterDrug}
                  options={asSelectOptions(drugOptions)}
                />
              </>
            }
            fields={[
              {
                name: "saltId",
                label: "Salt",
                type: "select",
                required: true,
                searchable: true,
                options: asSelectOptions(saltOptions),
              },
              {
                name: "drugId",
                label: "Drug",
                type: "select",
                required: true,
                searchable: true,
                options: asSelectOptions(drugOptions),
              },
            ]}
            onCreate={(values) =>
              ProductCatalogService.createSaltDrugMapping({
                saltId: values.saltId,
                drugId: values.drugId,
              })
            }
          />
        )}

        {innerTab === "schedule" && (
          <MasterPanel<ScheduleRow>
            title="Medicine Schedule"
            description="Regulatory schedules (H, H1, X, ...) a medicine can be classified under."
            addLabel="Add Schedule"
            columns={scheduleColumns}
            searchPlaceholder="Search schedules..."
            filters={statusFilterControl}
            refreshToken={`${activeFilter}|${reloadToken}`}
            fetchRows={fetchSchedules}
            fields={[
              {
                name: "name",
                label: "Schedule Name",
                type: "text",
                required: true,
                placeholder: "e.g. Schedule H1",
              },
              {
                name: "description",
                label: "Description",
                type: "textarea",
                placeholder: "What this schedule covers and how it is dispensed",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({
              name: row.name,
              description: row.description ?? "",
              isActive: row.isActive,
            })}
            onCreate={async (values) => {
              const res = await ProductCatalogService.createSchedule({
                name: values.name,
                description: values.description || null,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (row, values) => {
              const res = await ProductCatalogService.updateSchedule(row.id, {
                name: values.name,
                description: values.description || null,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}
      </div>
    </div>
  );
}
